import { useState, useEffect } from 'react';
import { Edit, Eye, Image, Plus, Trash2 } from 'lucide-react';
import {
  adminGetBlogPost, adminListBlogPosts, createBlogPost, deleteBlogPost,
  updateBlogPost, uploadToCloudinary,
} from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    author: 'Lalita Team',
    category: '',
    tags: '',
    status: 'draft',
    meta_title: '',
    meta_description: '',
  });

  const fetchPosts = async () => {
    try {
      const res = await adminListBlogPosts();
      setPosts(res.data || []);
    } catch (err) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const resetForm = () => {
    setForm({
      title: '', slug: '', excerpt: '', content: '', cover_image: '',
      author: 'Lalita Team', category: '', tags: '', status: 'draft',
      meta_title: '', meta_description: '',
    });
    setEditing(null);
  };

  const handleSlugify = (text) =>
    (text || '').toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s-]+/g, '-').replace(/^-|-$/g, '');

  const handleSubmit = async (e, publishOverride = null) => {
    if (e) e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags
        ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      status: publishOverride || form.status,
      slug: form.slug || handleSlugify(form.title),
      category: form.category || null,
    };
    if (!payload.title) {
      toast.error('Title is required');
      return;
    }
    try {
      if (editing) {
        await updateBlogPost(editing.id, payload);
        toast.success('Post updated');
      } else {
        await createBlogPost(payload);
        toast.success('Post created');
      }
      setShowModal(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = async (post) => {
    try {
      const res = await adminGetBlogPost(post.id);
      const p = res.data;
      setEditing(p);
      setForm({
        title: p.title || '',
        slug: p.slug || '',
        excerpt: p.excerpt || '',
        content: p.content || '',
        cover_image: p.cover_image || '',
        author: p.author || 'Lalita Team',
        category: p.category || '',
        tags: (p.tags || []).join(', '),
        status: p.status || 'draft',
        meta_title: p.meta_title || '',
        meta_description: p.meta_description || '',
      });
      setShowModal(true);
    } catch (err) {
      toast.error('Failed to load post');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this post permanently?')) return;
    try {
      await deleteBlogPost(id);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const result = await uploadToCloudinary(file, 'blog');
      if (result?.secure_url) {
        setForm((p) => ({ ...p, cover_image: result.secure_url }));
        toast.success('Cover image uploaded');
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-charcoal">Blog Posts</h2>
          <p className="text-charcoal-muted">Create and manage blog content for SEO and organic traffic</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-ubud-jungle hover:bg-ubud-jungle-dark" data-testid="blog-add-btn">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Views</TableHead>
              <TableHead>Published</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-charcoal-muted">Loading...</TableCell></TableRow>
            ) : posts.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-charcoal-muted">No posts yet. Write your first one!</TableCell></TableRow>
            ) : (
              posts.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium max-w-xs truncate">{p.title}</TableCell>
                  <TableCell className="text-sm text-charcoal-muted">{p.category || '—'}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${p.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {p.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{p.views || 0}</TableCell>
                  <TableCell className="text-sm text-charcoal-muted">
                    {p.published_at ? new Date(p.published_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {p.status === 'published' && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost" title="View on site">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(p)} data-testid={`blog-edit-${p.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(p.id)} data-testid={`blog-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Post' : 'New Blog Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => handleSubmit(e)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <Input
                placeholder="e.g., Why Canggu Is Bali's Hottest Investment Spot in 2026"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value, slug: p.slug || handleSlugify(e.target.value) }))}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">URL Slug</label>
                <Input
                  placeholder="auto-generated from title"
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: handleSlugify(e.target.value) }))}
                />
                <p className="text-xs text-charcoal-muted mt-1">/blog/{form.slug || 'your-slug'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <Input
                  placeholder="e.g., Investment, Lifestyle, Tips"
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Excerpt</label>
              <Textarea
                placeholder="1–2 sentence summary shown in blog list and search results."
                value={form.excerpt}
                onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cover Image</label>
              <div className="flex items-start gap-3">
                {form.cover_image ? (
                  <img src={form.cover_image} alt="cover" className="w-32 h-20 object-cover rounded-lg border" />
                ) : (
                  <div className="w-32 h-20 bg-warm-sand rounded-lg flex items-center justify-center text-charcoal-muted">
                    <Image className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    disabled={uploadingCover}
                  />
                  {uploadingCover && <p className="text-xs text-charcoal-muted">Uploading…</p>}
                  {form.cover_image && (
                    <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => setForm((p) => ({ ...p, cover_image: '' }))}>
                      Remove cover image
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Content *</label>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((p) => ({ ...p, content: html }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                <Input
                  placeholder="e.g., canggu, investment, leasehold"
                  value={form.tags}
                  onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Author</label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
                />
              </div>
            </div>

            <details className="border border-gray-200 rounded-lg p-3">
              <summary className="text-sm font-medium cursor-pointer">SEO Settings (optional override)</summary>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-charcoal-light">Meta Title (max 60 chars)</label>
                  <Input
                    maxLength={60}
                    placeholder="Defaults to post title"
                    value={form.meta_title}
                    onChange={(e) => setForm((p) => ({ ...p, meta_title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-charcoal-light">Meta Description (max 160 chars)</label>
                  <Textarea
                    maxLength={160}
                    placeholder="Defaults to excerpt"
                    value={form.meta_description}
                    onChange={(e) => setForm((p) => ({ ...p, meta_description: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
            </details>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button
                type="button"
                variant="outline"
                onClick={(e) => { setForm((p) => ({ ...p, status: 'draft' })); handleSubmit(e, 'draft'); }}
                data-testid="blog-save-draft"
              >
                Save as Draft
              </Button>
              <Button
                type="button"
                onClick={(e) => { setForm((p) => ({ ...p, status: 'published' })); handleSubmit(e, 'published'); }}
                className="bg-ubud-jungle hover:bg-ubud-jungle-dark"
                data-testid="blog-publish"
              >
                {form.status === 'published' && editing ? 'Update & Keep Published' : 'Publish Now'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard

export default BlogManagement;
